/**
 * TableAccountContent — tabs section of the table detail view.
 * Covers: CONTA / PAGOS / PEDIDOS / HISTÓRICO / NFC-E + print button.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/currency";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, MoreVertical, Printer, Trash2 } from "lucide-react";
import { printReceipt } from "@/utils/printService";
import { toast } from "sonner";
import { TableHistoricoTab } from "@/components/tables/TableHistoricoTab";
import { TableHistoricoContent } from "@/components/tables/TableHistoricoContent";

export function TableAccountContent({
  loading, orders, payments, account, total, finalTotal, appliedDiscount,
  user, restaurantData, table, handleEmitInvoice, processingInvoice,
  authAxios, onRefresh,
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("cuenta");
  const [cancellingItem, setCancellingItem] = useState(null);

  const handleCancelItem = async (orderId, itemIndex, itemName) => {
    if (!window.confirm(`Remover "${itemName}" da mesa?`)) return;
    setCancellingItem(`${orderId}-${itemIndex}`);
    try {
      await authAxios.post(`/tables/${table.id}/cancel-items`, {
        item_indices: [{ order_id: orderId, item_index: itemIndex }],
      });
      toast.success(`"${itemName}" removido`);
      if (onRefresh) onRefresh();
    } catch {
      toast.error("Erro ao remover item");
    }
    setCancellingItem(null);
  };

  const handlePrint = async () => {
    const allItems = orders.flatMap(o => o.items || []);
    const rawTotal = orders.reduce((s, o) => s + (o.total || 0), 0);
    const discount = appliedDiscount || 0;
    const orderData = {
      id: orders[0]?.id || "mesa",
      items: allItems,
      subtotal: rawTotal,
      discount_amount: discount,
      total: finalTotal ?? rawTotal,
      customer_name: table?.name || `Mesa ${table?.number}`,
      table_number: table?.number || table?.name,
      notes: "",
      source: "table",
      guests: account?.guests,
      per_person: account?.per_person,
    };
    const printStation = restaurantData?.print_stations?.[0] || { columns: 42 };
    const result = await printReceipt(orderData, printStation, restaurantData || {});
    if (result.success) {
      toast.success(result.method === "qz_tray" ? t("pos.printedDirect", "Impreso directo!") : t("pos.receiptPrinted", "Recibo impreso!"));
    } else {
      try {
        const { printReceiptSilent } = await import("@/utils/silentPrintService");
        await printReceiptSilent(orderData, { storeName: restaurantData?.name });
        toast.success(t("pos.receiptPrinted", "Recibo impreso!"));
      } catch {
        toast.error(t("pos.printError", "Error al imprimir") + ": " + (result.error || t("pos.checkPrinter", "Verifique la impresora")));
      }
    }
  };

  return (
    <>
      {/* Tabs bar */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex gap-6 overflow-x-auto">
          {[
            { key: "cuenta", label: "CONTA" },
            { key: "pagos", label: "PAGOS" },
            { key: "pedidos", label: "PEDIDOS" },
            { key: "historico", label: "HISTÓRICO" },
            { key: "nfce", label: "NFC-E" },
          ].map(tab => (
            <button
              key={tab.key}
              className={`pb-3 text-sm font-bold uppercase tracking-wide border-b-2 whitespace-nowrap ${activeTab === tab.key ? "text-gray-900 border-gray-900" : "text-gray-500 border-transparent hover:text-gray-700"}`}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`table-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <LoadingScreen /> : (
        <>
          {activeTab === "cuenta" && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
                    <th className="text-center p-3 font-medium w-12">Ctd</th>
                    <th className="text-left p-3 font-medium">Descripción</th>
                    <th className="text-right p-3 font-medium">Valor</th>
                    <th className="w-10 p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.flatMap(order => order.items?.map((item, idx) => (
                    <tr key={`${order.id}-${idx}`} className="hover:bg-red-50 group">
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3">{item.product_name}</td>
                      <td className="p-3 text-right">
                        <div>{formatPrice(item.quantity * item.unit_price)}</div>
                        <div className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })} {new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                      <td className="p-2 text-center">
                        {authAxios && (
                          <button
                            onClick={() => handleCancelItem(order.id, idx, item.product_name)}
                            disabled={cancellingItem === `${order.id}-${idx}`}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-100 transition-all"
                            title="Remover item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )) || [])}
                  {orders.length === 0 && (
                    <tr><td colSpan={3} className="p-8 text-center text-gray-400">Sin items</td></tr>
                  )}
                </tbody>
                {orders.length > 0 && (
                  <tfoot className="border-t border-gray-200">
                    <tr>
                      <td colSpan={2} className="p-3 text-right font-medium text-gray-600">Subtotal</td>
                      <td className="p-3 text-right font-medium">{formatPrice(total)}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="p-3 text-right font-bold text-gray-900">Total</td>
                      <td className="p-3 text-right font-bold">{formatPrice(finalTotal)}</td>
                    </tr>
                    {account?.guests && (
                      <>
                        <tr>
                          <td colSpan={2} className="p-3 text-right text-xs font-medium text-gray-600">Personas</td>
                          <td className="p-3 text-right text-xs font-medium">{account.guests}</td>
                        </tr>
                        <tr>
                          <td colSpan={2} className="p-3 text-right text-xs font-medium text-gray-600">Importe por persona</td>
                          <td className="p-3 text-right text-xs font-medium">{formatPrice(account.per_person || 0)}</td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {activeTab === "pagos" && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
                    <th className="text-left p-3 font-medium">Hora</th>
                    <th className="text-left p-3 font-medium">Pago</th>
                    <th className="text-center p-3 font-medium">Cantidad</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Responsable</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map((p, idx) => (
                    <tr key={idx}>
                      <td className="p-3 text-gray-600">{p.date ? new Date(p.date).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                      <td className="p-3">Cabal ({p.method === "card" ? "Crédito" : p.method === "cash" ? "Efectivo" : p.method})</td>
                      <td className="p-3 text-center font-medium">{formatPrice(p.amount)}</td>
                      <td className="p-3"><Badge className={p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"}>{p.status === "paid" ? "Acepto" : "Pendiente"}</Badge></td>
                      <td className="p-3 text-gray-600">KAISO</td>
                      <td className="p-3"><MoreVertical className="w-4 h-4 text-gray-400" /></td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400">Sin pagos registrados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "pedidos" && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
                    <th className="text-left p-3 font-medium">Pedido</th>
                    <th className="text-left p-3 font-medium">Fecha</th>
                    <th className="text-center p-3 font-medium">Items</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="p-3 font-mono font-bold text-emerald-700">#{order.id.slice(-6).toUpperCase()}</td>
                      <td className="p-3 text-gray-600">{new Date(order.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-3 text-center">{order.items?.length || 0}</td>
                      <td className="p-3">
                        <Badge className={
                          order.status === "delivered" ? "bg-emerald-100 text-emerald-700" :
                          order.status === "cancelled" ? "bg-gray-100 text-gray-500" :
                          order.status === "pending_payment" ? "bg-red-100 text-red-700 border border-red-200" :
                          "bg-yellow-100 text-yellow-700"
                        }>
                          {order.status === "delivered" ? "Entregue" :
                           order.status === "cancelled" ? "Cancelado" :
                           order.status === "pending_payment" ? "⚠ Aguarda Pagamento" :
                           "Em curso"}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-bold">{formatPrice(order.total)}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-gray-400">Sin pedidos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "historico" && (
            <TableHistoricoContent user={user} tableId={table?.id} />
          )}

          {activeTab === "nfce" && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Emissão de Nota Fiscal
                </h3>
                <p className="text-sm text-gray-600 mb-4">Emita a nota fiscal (NFC-e, Vendus ou Cegid) para esta mesa.</p>
                <div className="flex gap-3 mb-6">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2" onClick={handleEmitInvoice} disabled={orders.length === 0 || processingInvoice} data-testid="nfce-emit-btn">
                    <FileText className="w-4 h-4" />
                    {processingInvoice ? "Emitindo..." : "Emitir NFC-e / Vendus"}
                  </Button>
                </div>
                <div className="border-t pt-4">
                  <h4 className="text-sm font-bold text-gray-700 mb-3">Documentos Fiscais Emitidos</h4>
                  {account?.invoices?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs uppercase text-gray-400">
                          <th className="text-left p-2">Nº Documento</th>
                          <th className="text-left p-2">Data</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-right p-2">Valor</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.invoices.map((inv, idx) => (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="p-2 font-mono">{inv.number || inv.id}</td>
                            <td className="p-2">{inv.date ? new Date(inv.date).toLocaleDateString("pt-BR") : "-"}</td>
                            <td className="p-2"><Badge className="bg-emerald-100 text-emerald-700">{inv.status || "Emitido"}</Badge></td>
                            <td className="p-2 text-right font-bold">{formatPrice(inv.total)}</td>
                            <td className="p-2">{inv.url && <a href={inv.url} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline text-xs">Ver</a>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-400 text-sm">Nenhum documento fiscal emitido para esta mesa</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Print button */}
      <div className="mt-4">
        <Button className="bg-orange-500 hover:bg-orange-600 text-white gap-2 font-bold" onClick={handlePrint} data-testid="print-account-btn">
          <Printer className="w-4 h-4" /> {t("common.print", "IMPRIMIR")}
        </Button>
      </div>
    </>
  );
}